import { z } from "zod";
import { FitViewOptions, Node } from "@xyflow/react";
import { atom } from "jotai";
import { DEFAULT_NODE_WIDTH, PATH_NODE_WIDTH } from "@zapier/canvas-constants";
import {
  nodesAtom,
  AnnotationNode,
  tourIntroStepAtom,
  isGeneratingTourAtom,
} from "@zapier/canvas-state";
import { tourStepSchema } from "../../components/CanvasComposerHydrator";

export const renderAnnotationNodesSetter = atom(
  null,
  (
    _,
    set,
    {
      tourStepsData,
      fitView,
    }: {
      tourStepsData: z.infer<typeof tourStepSchema>[];
      fitView: (fitViewOptions?: FitViewOptions) => Promise<boolean>;
    },
  ) => {
    // Extract intro step (first element) and store it in separate atom
    const introStep = tourStepsData.find((data) => data.stepNumber === 0) || {
      header: "Welcome to the tour",
      description:
        "This tour will guide you through the key features of the canvas. Let's get started!",
    };

    const tourSteps = tourStepsData.filter((data) => data.stepNumber > 0);
    const totalTourSteps = tourSteps.length;

    set(tourIntroStepAtom, {
      header: introStep.header,
      description: introStep.description,
      totalSteps: totalTourSteps,
    });

    // Map remaining steps (starting from stepNumber 1)
    const annotationNodes: AnnotationNode[] = tourSteps.map(
      (n, index: number) =>
        ({
          id: `_ANNOTATION_${index + 1}`,
          type: "annotation",

          position: {
            x: 0, // Will be adjusted in setNodes
            y: 0,
          },

          parentId: n.parentId,
          hidden: true,
          draggable: false,
          deletable: false,
          selectable: false,
          width: DEFAULT_NODE_WIDTH,

          data: {
            header: n.header,
            description: n.description,
            stepNumber: n.stepNumber,
            totalSteps: totalTourSteps,
            isLastStep: index === tourSteps.length - 1,
          },
        }) as AnnotationNode,
    );

    set(nodesAtom, (prevNodes: Node[]) => {
      // Remove any existing annotation nodes
      const nonAnnotationNodes = prevNodes.filter(
        (node) => node.type !== "annotation",
      );

      // Adjust positions based on parent nodes
      const adjustedAnnotationNodes = annotationNodes.map(
        (node: AnnotationNode) => {
          const parentNode = nonAnnotationNodes.find(
            (n) => n.id === node.parentId,
          );
          const isParentAPathNode = parentNode?.type === "path";
          const xOffset = isParentAPathNode
            ? -(DEFAULT_NODE_WIDTH - PATH_NODE_WIDTH) / 2
            : 0;

          return {
            ...node,
            position: {
              ...node.position,
              x: xOffset,
            },
          };
        },
      );

      return [...nonAnnotationNodes, ...adjustedAnnotationNodes];
    });

    // Wait for nodes to be added before panning and zooming
    setTimeout(() => {
      fitView({
        includeHiddenNodes: false,
        padding: 0.1,
        minZoom: 0.3,
        maxZoom: 0.9,
        duration: 200,
      });

      set(isGeneratingTourAtom, false);

      // Remove tour query parameter if it exists, so on page refresh, tour won't start again
      const url = new URL(window.location.href);
      url.searchParams.delete("tour");
      window.history.replaceState({}, "", url.toString());
    }, 0);

    return {
      content: [
        {
          type: "text",
          text: `Tour started!`,
        },
      ],
    };
  },
);

renderAnnotationNodesSetter.debugLabel = "renderAnnotationNodesSetter";
